import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");

  // Data
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [stats, setStats] = useState(null);

  async function login() {
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      setAuthed(true);
    } catch {
      setError("Wrong password.");
    }
  }

  const loadEntries = useCallback(async (newSkip = 0, searchVal = search) => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        params: { token, skip: newSkip, limit: 50, search: searchVal },
      });
      setEntries((prev) => newSkip === 0 ? res.data.entries : [...prev, ...res.data.entries]);
      setTotal(res.data.total);
      setSkip(newSkip + res.data.entries.length);
    } catch { /* ignore */ }
  }, [token, search]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/stats`, { params: { token } });
      setStats(res.data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (authed) {
      loadEntries(0, search);
      loadStats();
    }
  }, [authed, loadEntries, loadStats]);

  useEffect(() => {
    if (authed) {
      const t = setTimeout(() => { loadEntries(0, search); }, 300);
      return () => clearTimeout(t);
    }
  }, [search, authed, loadEntries]);

  async function deleteEntry(id) {
    if (!window.confirm("Remove this email from the waitlist?")) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, { params: { token } });
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => t - 1);
    } catch { /* ignore */ }
  }

  // Login screen
  if (!authed) {
    return (
      <div className="admin-login" data-testid="admin-login-screen">
        <div className="nav-dot" style={{ width: 10, height: 10 }} />
        <h2 style={{ fontFamily: "var(--serif)", fontSize: 24, color: "var(--cream)", marginBottom: 8 }}>
          Hypha Admin
        </h2>
        <input
          className="admin-login-input"
          type="password"
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") login(); }}
          data-testid="admin-password-input"
        />
        <button className="hero-btn" onClick={login} data-testid="admin-login-btn">
          Sign in
        </button>
        {error && <p style={{ color: "rgba(226,75,74,0.8)", fontFamily: "var(--mono)", fontSize: 13 }}>{error}</p>}
        <button
          className="nav-link"
          style={{ marginTop: 20, background: "none", border: "none", cursor: "pointer" }}
          onClick={() => navigate("/")}
        >
          Back to landing page
        </button>
      </div>
    );
  }

  return (
    <div className="admin-wrap" data-testid="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="nav-dot" style={{ width: 8, height: 8 }} />
          <h1 className="admin-title">Hypha Admin</h1>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button className="nav-link" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => navigate("/")}>
            Landing page
          </button>
          <button className="nav-link" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => navigate("/app")}>
            App
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="admin-stat-grid" data-testid="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="stat-value">{stats.total_waitlist}</div>
            <div className="stat-label">Total waitlist</div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-value">{stats.total_push_subscriptions || 0}</div>
            <div className="stat-label">Push subscribers</div>
          </div>
          {stats.daily_signups.length > 0 && (
            <div className="admin-stat-card">
              <div className="stat-value">{stats.daily_signups[0]?.count || 0}</div>
              <div className="stat-label">Today's signups</div>
            </div>
          )}
          {stats.sources.map((s) => (
            <div className="admin-stat-card" key={s.source}>
              <div className="stat-value">{s.count}</div>
              <div className="stat-label">From {s.source}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <input
        className="history-search"
        type="search"
        placeholder="Search emails..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
        data-testid="admin-search-input"
      />

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table className="admin-table" data-testid="admin-waitlist-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Source</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>{e.email}</td>
                <td>{e.name || "—"}</td>
                <td>{e.source || "landing"}</td>
                <td>{e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  <button
                    className="admin-delete-btn"
                    onClick={() => deleteEntry(e.id)}
                    data-testid={`admin-delete-${e.id}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--cream-faint)", fontStyle: "italic" }}>
                  {search ? "No emails match that search." : "No waitlist signups yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {entries.length < total && (
        <button
          className="load-more-btn"
          onClick={() => loadEntries(skip, search)}
          style={{ marginTop: 16 }}
          data-testid="admin-load-more-btn"
        >
          Load more ({total - entries.length} remaining)
        </button>
      )}

      {/* Footer */}
      <div style={{ textAlign: "center", marginTop: 48, paddingBottom: 24 }}>
        <p style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300, letterSpacing: "0.12em", color: "rgba(29,158,117,0.35)" }}>
          MYHYPHA.AI ADMIN
        </p>
      </div>
    </div>
  );
}
